/*
This code for rendering a dynamic, interactive sidebar for a web
application, featuring expandable navigation links that reflect
the current route and integrate with global state management
for responsive updates and user notifications.
 */

import { useEffect, useMemo, useState } from "react";
import { ROUTES } from "../../utils/constants";
import { Link, useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { useSidebar } from "./sidebarContext";
import "./sidebar.scss";
import createTheme from "@mui/material/styles/createTheme";
import { ThemeProvider } from "@emotion/react";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import BackHandOutlinedIcon from "@mui/icons-material/BackHandOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { isReportsUser, UserRole } from "../../utils/permissions";
import { useSelector } from "react-redux";
import { getAuth } from "../../redux/auth/authSlice";
import { useDialogBoxContext, divSettingsState } from "../../components/decisionDialogBox/decisionDialogBoxContext";
import { decisionDialogBoxTitle, decisionDialogBoxMessage } from "../../components/decisionDialogBox/decisionDialogBox";

export const Sidebar = () => {
  interface MenuItem {
    label: string;
    icon: React.ReactElement;
    link: string;
    active: boolean;
    sublist?: MenuItem[];
  }
  const currentPath = window?.location?.pathname;
  const { userInfo } = useSelector(getAuth);
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const { isExpanded } = useSidebar();
  const navigate = useNavigate();
  const { showDecisionDialogBox } = useDialogBoxContext();

  /* Determine the user's role based on their groups, environment, and division */
  const userRoles: UserRole[] = useMemo(() => {
    if (!userInfo || !userInfo.userRoles) return ["None"];
    return userInfo.userRoles;
  }, [userInfo]);

  /*
   * Memoized array of menu items for the sidebar, each containing a label, icon, link, and active status.
   * This array is used to render the sidebar navigation with appropriate icons and routes.
   */
  const allMenuItems: MenuItem[] = useMemo(
    () => [
      {
        label: ROUTES.closings.myConcierge.label,
        icon: <SpaceDashboardOutlinedIcon />,
        link: ROUTES.closings.myConcierge.route,
        active: false,
      },
      {
        label: ROUTES.closings.buyerListing.label,
        icon: <GroupOutlinedIcon />,
        link: ROUTES.closings.buyerListing.route,
        active: false,
      },
      {
        label: ROUTES.closings.tasks.label,
        icon: <AssignmentTurnedInOutlinedIcon />,
        link: ROUTES.closings.tasks.route,
        active: false,
      },
      {
        label: ROUTES.closings.conciergeAssignment.label,
        icon: <BackHandOutlinedIcon />,
        link: ROUTES.closings.conciergeAssignment.route,
        active: false,
      },
      {
        label: ROUTES.closings.reports.label,
        icon: <ReceiptLongOutlinedIcon />,
        link: ROUTES.closings.reports.route,
        active: false,
      },
    ],
    []
  );

  /*
   * Filters the menu items based on the user's role.
   * Returns specific menu items for "Reports" role, all items for admin roles, and an empty array for other roles.
   */
  const filteredMenuItems: MenuItem[] = useMemo(() => {
    if (userRoles.length === 0) return [];
    if (isReportsUser(userRoles)) {
      return allMenuItems.filter(
        (item) => item.label === ROUTES.closings.reports.label
      );
    } 
    return allMenuItems;
    
  }, [userRoles, allMenuItems]);

  /*
   * Updates the menu items to include an `active` property based on the current path.
   */
  useEffect(() => {
    const updatedMenu = filteredMenuItems.map((item) => ({
      ...item,
      active: item.link === currentPath,
    }));
    setMenu(updatedMenu);

    if (filteredMenuItems.length === 0) {
      if (isReportsUser(userRoles)) {
        navigate(ROUTES.closings.reports.route);
      } else {
        navigate(ROUTES.closings.myConcierge.route);
      }
      return;
    }
  }, [currentPath, filteredMenuItems, navigate, userRoles]);

  const theme = createTheme({
    typography: {
      fontFamily: "karla, sans-serif",
      fontSize: 14,
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <aside className={`sidebar ${!isExpanded ? "expand-sidebar" : ""}`}>
        <div>
          {menu.map((list, index) => (
            <Link
              className={`expand-list ${list.active ? "sublist-active" : ""}`}
              key={list.label}
              to={list.link}
              onClick={(e) => {
                if (divSettingsState.unsaved) {
                  e.preventDefault();
                  showDecisionDialogBox(
                    true,
                    decisionDialogBoxTitle,
                    decisionDialogBoxMessage,
                    () => {
                      divSettingsState.save();
                      divSettingsState.unsaved = false;
                      navigate(list.link);
                    },
                    () => {
                      divSettingsState.cancel();
                      divSettingsState.unsaved = false;
                      navigate(list.link);
                    }
                  );
                  return;
                }
                const updatedMenu = menu.map((item) => ({
                  ...item,
                  active: item.link === list.link,
                }));
                setMenu(updatedMenu);
              }}
              style={{ textDecoration: "none", color: "#003c77" }}
            >
              {isExpanded ? (
                <div>{list.icon}</div>
              ) : (
                <Tooltip title={list.label} arrow placement="right-start">
                  <div>{list.icon}</div>
                </Tooltip>
              )}
              {isExpanded && (
                <div style={{ marginLeft: "16px" }}>{list.label}</div>
              )}
            </Link>
          ))}
        </div>
      </aside>
    </ThemeProvider>
  );
};
